import { Injectable } from '@nestjs/common';
import { ErrorResponse } from '@gtpl/shared-models/common-models';
import { InjectRepository } from '@nestjs/typeorm';
import { Entity, Repository } from 'typeorm';
import { AllRecruitmentActivitiesResponse, RecruitmentActivitiesResponse } from '@gtpl/shared-models/hrms';
import { RecruitmentActivitiesDTO } from './dto/recruitment-activities.dto';
import { RecruitmentActivitesAdapter } from './dto/recruitment-activities.adapter';
import { RecruitmentActivitiesRequest } from './dto/recruitment-activities.request';
import { RecruitmentActivities } from './entity/recruitment-activities.entity';

@Injectable()
export class RecruitmentActivitiesService {
    constructor(
        @InjectRepository(RecruitmentActivities)
        private repository: Repository<RecruitmentActivities>,
        private adapter: RecruitmentActivitesAdapter,
    ) { }

    async getRecruitmentActivity(recruitmentActivityId: number): Promise<RecruitmentActivities> {
        const response = await this.repository.findOne({
            where: { recruitmentActivityId: recruitmentActivityId },
        });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async createRecruitmentActivity(data: RecruitmentActivitiesDTO, isUpdate: boolean): Promise<RecruitmentActivitiesResponse> {
        try {
            if (!isUpdate) {
                const entity = await this.getRecruitmentActivity(data.recruitmentActivityId);
                if (entity) {
                    throw new ErrorResponse(11104, 'RecruitmentActivity already exits');
                }
            } else {
                const RecruitmentActivitysEntity = await this.getRecruitmentActivity(data.recruitmentActivityId);
                if (RecruitmentActivitysEntity) {
                    if (RecruitmentActivitysEntity.recruitmentActivityId != data.recruitmentActivityId) {
                        throw new ErrorResponse(11104, 'RecruitmentActivity already exits');
                    }
                }
            }
            // //duplicates
            // const isUserAlreadyExist = await this.repository.findOne({ where: { candidateName: data.candidateName } });
            // if (isUserAlreadyExist?.recruitmentActivityId != data.recruitmentActivityId) {
            //     throw new ErrorResponse(11104, 'Candidate Name alreday exist with given Name');
            // }
            const convertedDto: RecruitmentActivities = this.adapter.convertDtoToEntity(data);
            const savedRecruitmentActivity: RecruitmentActivities = await this.repository.save(convertedDto);
            const convertedRecruitmentActivity: RecruitmentActivitiesDTO = this.adapter.convertEntityToDto(savedRecruitmentActivity);

            if (convertedRecruitmentActivity) {
                const response = new RecruitmentActivitiesResponse(true, isUpdate ? 11100 : 11101, isUpdate ? 'RecruitmentActivity updated successfully' : 'RecruitmentActivity created successfully', convertedRecruitmentActivity);
                return response;
            } else {
                throw new ErrorResponse(11106, 'RecruitmentActivity is saved but error while converting it into DTO');
            }
        } catch (error) {
            return error;
        }
    }

    async getAllRecruitmentActivities(): Promise<AllRecruitmentActivitiesResponse> {
        try {
            const RecruitmentActivitys: RecruitmentActivitiesDTO[] = [];
            const entities: RecruitmentActivities[] = await this.repository.find({
                order: { recruitmentActivityId: 'DESC' },
            });
            if (entities.length > 0) {
                entities.forEach(Entity => {
                    const RecruitmentActivities: RecruitmentActivitiesDTO = this.adapter.convertEntityToDto(Entity);
                    RecruitmentActivitys.push(RecruitmentActivities);
                });
                const response = new AllRecruitmentActivitiesResponse(true, 11208, 'RecruitmentActivities retrieved successfully', RecruitmentActivitys);
                return response;
            } else {
                throw new ErrorResponse(99998, 'No Records Found');
            }
        } catch (error) {
            return error;
        }
    }

    async getRecruitmentActivityById(id: number): Promise<RecruitmentActivities> {
        const response = await this.repository.findOne({
            where: { recruitmentActivityId: id },
        });
        if (response) {
            return response;
        } else {
            return null;
        }
    }

    async activateOrDeactivateRecruitmentActivity(req: RecruitmentActivitiesRequest): Promise<RecruitmentActivitiesResponse> {
        try {
            const RecruitmentActivitysExists = await this.getRecruitmentActivityById(req.recruitmentActivityId);
            if (RecruitmentActivitysExists) {
                if (req.versionFlag != RecruitmentActivitysExists.versionFlag) {
                    throw new ErrorResponse(10113, 'Someone updated the current RecruitmentActivity information. Refresh and try again');
                } else {
                    const RecruitmentActivitystatus = await this.repository.update(
                        { recruitmentActivityId: req.recruitmentActivityId },
                        { isActive: req.isActive, updatedUser: req.updatedUser },
                    );
                    if (RecruitmentActivitysExists.isActive) {
                        if (RecruitmentActivitystatus.affected) {
                            const response: RecruitmentActivitiesResponse = new RecruitmentActivitiesResponse(true, 10115, 'RecruitmentActivity is de-activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10111, 'RecruitmentActivity is already de-activated');
                        }
                    } else {
                        if (RecruitmentActivitystatus.affected) {
                            const response: RecruitmentActivitiesResponse = new RecruitmentActivitiesResponse(true, 10114, 'RecruitmentActivity is activated successfully');
                            return response;
                        } else {
                            throw new ErrorResponse(10112, 'RecruitmentActivity is already activated');
                        }
                    }
                }
            } else {
                throw new ErrorResponse(99998, 'No Records Found');
            }
        } catch (error) {
            return error;
        }
    }

    async getAllActiveRecruitmentActivities(): Promise<AllRecruitmentActivitiesResponse> {
        try {
            const RecruitmentActivitys: RecruitmentActivitiesDTO[] = [];
            const entities: RecruitmentActivities[] = await this.repository.find({
                order: { recruitmentActivityId: 'ASC' }, where: { isActive: true },
            });
            if (entities.length > 0) {
                entities.forEach(Entity => {
                    const RecruitmentActivities: RecruitmentActivitiesDTO = this.adapter.convertEntityToDto(Entity);
                    RecruitmentActivitys.push(RecruitmentActivities);
                });
                const response = new AllRecruitmentActivitiesResponse(true, 11208, 'RecruitmentActivities retrieved successfully', RecruitmentActivitys);
                return response;
            } else {
                throw new ErrorResponse(99998, 'No Records Found');
            }
        } catch (error) {
            return error;
        }
    }
}
